import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, membrosApi, financeiroApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  Church,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Cake,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [graficoData, setGraficoData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, anivRes, graficoRes] = await Promise.all([
        dashboardApi.getStats(user?.igreja_id),
        membrosApi.aniversariantesMes(new Date().getMonth() + 1, user?.igreja_id),
        financeiroApi.getGraficoMensal({ igreja_id: user?.igreja_id }),
      ]);
      setStats(statsRes.data);
      setAniversariantes(anivRes.data || []);
      setGraficoData(graficoRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'sky' }) => (
    <Card className="stat-card card-hover" data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-2 font-heading">{value}</p>
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                {trend === 'up' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-${color}-50`}>
            <Icon className={`w-6 h-6 text-${color}-500`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="stat-card">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const resumo = stats?.resumo_financeiro || {};

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">
          Bem-vindo, {user?.nome}! Aqui está o resumo da sua igreja.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total de Membros"
          value={stats?.total_membros || 0}
          icon={Users}
          color="sky"
        />
        <StatCard
          title="Unidades"
          value={stats?.total_unidades || 1}
          icon={Church}
          color="violet"
        />
        <StatCard
          title="Entradas do Mês"
          value={formatCurrency(resumo.total_entradas)}
          icon={TrendingUp}
          trend="up"
          trendValue="Este mês"
          color="emerald"
        />
        <StatCard
          title="Saldo Atual"
          value={formatCurrency(resumo.saldo)}
          icon={DollarSign}
          color="amber"
        />
      </div>

      {/* Charts and Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Financial Chart */}
        <Card className="lg:col-span-2 stat-card" data-testid="financial-chart">
          <CardHeader>
            <CardTitle className="card-title">Movimento Financeiro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={graficoData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="nome_mes" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(value) => `R$${value}`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="entradas" name="Entradas" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="saidas" name="Saídas" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Birthdays */}
        <Card className="stat-card" data-testid="aniversariantes">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="card-title flex items-center gap-2">
              <Cake className="w-5 h-5 text-amber-500" />
              Aniversariantes do Mês
            </CardTitle>
            <Badge variant="secondary" className="bg-amber-50 text-amber-700">
              {aniversariantes.length}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {aniversariantes.length > 0 ? (
                aniversariantes.slice(0, 10).map((membro) => {
                  const dataNasc = membro.data_nascimento ? new Date(membro.data_nascimento + 'T00:00:00') : null;
                  return (
                    <div
                      key={membro.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                        <span className="text-sky-600 font-semibold text-sm">
                          {membro.nome?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{membro.nome}</p>
                        <p className="text-xs text-slate-500">
                          {dataNasc ? format(dataNasc, "dd 'de' MMMM", { locale: ptBR }) : '-'}
                        </p>
                      </div>
                      {dataNasc && dataNasc.getDate() === new Date().getDate() && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">Hoje!</Badge>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Cake className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nenhum aniversariante este mês</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card bg-gradient-to-br from-sky-50 to-white" data-testid="card-dizimos">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-sky-100">
                <DollarSign className="w-6 h-6 text-sky-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Dízimos do Mês</p>
                <p className="text-2xl font-bold text-slate-900 font-heading">
                  {formatCurrency(resumo.total_dizimos)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card bg-gradient-to-br from-emerald-50 to-white" data-testid="card-ofertas">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-100">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ofertas do Mês</p>
                <p className="text-2xl font-bold text-slate-900 font-heading">
                  {formatCurrency(resumo.total_ofertas)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card bg-gradient-to-br from-amber-50 to-white" data-testid="card-despesas">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100">
                <TrendingDown className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Despesas do Mês</p>
                <p className="text-2xl font-bold text-slate-900 font-heading">
                  {formatCurrency(resumo.total_despesas)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
